/*

  KEN BURNS SLIDESHOW

  image slideshow
  slow zoom / pan
  fade to/from black

*/

/* global window, requestAnimationFrame, cancelAnimationFrame */

import THREE from 'three';
import {gsap} from 'gsap';
import {SchemaType} from '../SlideshowDefinition';
import * as Post from './post';
import {Util} from './lib/Util';

let domRef: HTMLDivElement;
let canvasRef: HTMLCanvasElement;
let props: SchemaType;

let camera: THREE.OrthographicCamera;
let scene: THREE.Scene;
let renderer: THREE.WebGLRenderer;
let quadMaterial: THREE.MeshBasicMaterial;
let screenQuad: THREE.Mesh;
let groupHolder: THREE.Group;
let textureLoader: THREE.TextureLoader;
let imageIndex = -1;
let imageCount: number;
let textures: Array<THREE.Texture> = [];
let raf: number;
let transitionCall: gsap.core.Tween;
let isTransitioning: boolean;
let zoomingIn = true;
const frustumSize = 2; //camera area is 2 by 2 units
let imgAspect = 1;

export async function init(
  _domRef: HTMLDivElement,
  _canvasRef: HTMLCanvasElement,
  _props: SchemaType
): Promise<void> {
  domRef = _domRef;
  canvasRef = _canvasRef;
  props = _props;

  //load textures
  textureLoader = new THREE.TextureLoader();
  await loadImages(props.images);

  //threejs world
  renderer = new THREE.WebGLRenderer({
    antialias: true,
    canvas: canvasRef,
  });
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setClearColor(new THREE.Color(props.bgColor));

  camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  scene = new THREE.Scene();
  groupHolder = new THREE.Group();
  scene.add(groupHolder);

  const screenGeom = new THREE.PlaneGeometry(frustumSize, frustumSize);

  //IMAGE QUAD
  quadMaterial = new THREE.MeshBasicMaterial();
  quadMaterial.transparent = true;
  quadMaterial.opacity = 0;

  screenQuad = new THREE.Mesh(screenGeom, quadMaterial);
  groupHolder.add(screenQuad);

  Post.init(renderer, props);

  window.addEventListener('resize', onResize, false);
  onResize();

  update();

  canvasRef.addEventListener('click', onClick);

  //fade up
  gsap.fromTo(renderer.domElement, {opacity: 0}, {opacity: 1, duration: 0.5});
  gsap.delayedCall(1, goNext);
}

function onResize(): void {
  //resize canvas to fill wrapper dom element
  const w = domRef.offsetWidth;
  const h = domRef.offsetHeight;
  const dpr = window.devicePixelRatio;
  const canvasAspect = w / h;

  camera.left = (-frustumSize * canvasAspect) / 2;
  camera.right = (frustumSize * canvasAspect) / 2;
  camera.top = frustumSize / 2;
  camera.bottom = -frustumSize / 2;
  camera.updateProjectionMatrix();

  let scl;
  if (canvasAspect < imgAspect) {
    scl = 1;
  } else {
    scl = canvasAspect / imgAspect;
  }
  groupHolder.scale.set(scl, scl, scl);
  renderer.setSize(w, h);
  renderer.setPixelRatio(dpr);
  Post.resize(w, h, dpr);
}

function update(): void {
  raf = requestAnimationFrame(update);
  Post.render(renderer, scene, camera);
}

function goNext(): void {
  if (isTransitioning) return;
  const isMultiImage = textures.length > 1;

  if (isMultiImage) {
    imageIndex++;
  } else {
    imageIndex = 0;
  }
  imageIndex = Util.modWrap(imageIndex, imageCount);
  showImage(imageIndex);
  if (transitionCall) transitionCall.kill();
  // Only continue if there are more than 1 images
  if (isMultiImage) {
    transitionCall = gsap.delayedCall(props.imageDuration, goNext);
  }
}

function showImage(nextIndex: number): void {
  isTransitioning = true;
  //fade down
  gsap.to(quadMaterial, {
    opacity: 0,
    duration: props.transitionDuration / 2,
    ease: 'none',
    onComplete: () => {
      isTransitioning = false;
      zoomingIn = !zoomingIn;
      //switch image
      const nextTexture = textures[nextIndex];
      if (nextTexture) {
        quadMaterial.map = nextTexture;
        imgAspect = nextTexture.image.width / nextTexture.image.height;
        screenQuad.scale.x = imgAspect;
        quadMaterial.needsUpdate = true;
      }
      onResize();
      fadeUpAndPan();
    },
  });
}

function fadeUpAndPan(): void {
  //fade up
  gsap.to(quadMaterial, {
    opacity: 1,
    duration: props.transitionDuration / 2,
    ease: 'none',
  });

  //do a random zoom, alternating in and out
  //pick random zoom amount within maxZoom
  const zoomScl = Util.randomRange((props.maxZoom - 1) / 2 + 1, props.maxZoom);
  const startScl = zoomingIn ? 1 : zoomScl;
  const endScl = zoomingIn ? zoomScl : 1;
  gsap.fromTo(
    screenQuad.scale,
    {x: startScl * imgAspect, y: startScl, z: startScl},
    {
      x: endScl * imgAspect,
      y: endScl,
      z: endScl,
      duration: props.imageDuration,
      ease: 'none',
    }
  );

  //do a random pan keeping image inside frame
  const maxPanAmt = new THREE.Vector2();
  maxPanAmt.y = zoomScl - 1;
  maxPanAmt.x = maxPanAmt.y * imgAspect;
  const panPos = new THREE.Vector2();
  panPos.x = Util.randomRange(-maxPanAmt.x, maxPanAmt.x);
  panPos.y = Util.randomRange(-maxPanAmt.y, maxPanAmt.y);
  const noPanPos = new THREE.Vector2(0, 0);
  const startPos = zoomingIn ? noPanPos : panPos;
  const endPos = zoomingIn ? panPos : noPanPos;
  gsap.fromTo(
    screenQuad.position,
    {x: startPos.x, y: startPos.y},
    {
      x: endPos.x,
      y: endPos.y,
      duration: props.imageDuration,
      ease: 'none',
    }
  );
}

function onClick(): void {
  if (!props.interactive || textures.length < 2) return;
  goNext();
}

async function loadImages(imageURLs: Array<string>): Promise<void> {
  textures = [];
  imageCount = imageURLs.length;
  //preload 1st 2 textures
  for (let i = 0; i < imageURLs.length; i++) {
    const imageURL = imageURLs[i];
    if (!imageURL) {
      continue;
    } else if (i <= 1) {
      // Awaiting only the first two image loads
      // eslint-disable-next-line no-await-in-loop
      textures.push(await textureLoader.loadAsync(imageURL));
    } else {
      textures.push(textureLoader.load(imageURL));
    }
  }
}

export function toggleVisibility(isVisible: boolean): void {
  //fade out / in and toggle render loop
  if (isVisible) {
    //show
    gsap.fromTo(domRef, {opacity: 0}, {opacity: 1, duration: 0.5});
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(update);
    gsap.globalTimeline.play();
  } else {
    //hide
    gsap.fromTo(
      domRef,
      {opacity: 1},
      {
        opacity: 0,
        duration: 0.5,
        onComplete: () => {
          if (raf) cancelAnimationFrame(raf);
          gsap.globalTimeline.pause();
        },
      }
    );
  }
}

export function dispose(): void {
  window.removeEventListener('resize', onResize);
  cancelAnimationFrame(raf);
  canvasRef.removeEventListener('click', onClick);
  if (renderer) renderer.dispose();
}
